import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  tone?: 'default' | 'danger';
}

interface ConfirmState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  tone: 'default' | 'danger';
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

interface ConfirmProviderProps {
  children: React.ReactNode;
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20000,
  },
  panel: {
    width: 460,
    maxWidth: '92vw',
    backgroundColor: '#0d1117',
    border: '2px solid #30363d',
    color: '#e6edf3',
    fontFamily: 'var(--font-mono), monospace',
  },
  header: {
    padding: '12px 14px',
    borderBottom: '1px solid #30363d',
    backgroundColor: '#161b22',
    fontSize: 13,
    fontWeight: 700,
  },
  body: {
    padding: '14px',
    fontSize: 12,
    color: '#c9d1d9',
    lineHeight: 1.4,
  },
  footer: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
    padding: '12px 14px',
    borderTop: '1px solid #30363d',
    backgroundColor: '#0d1117',
  },
  button: {
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 700,
    borderRadius: 0,
  },
  cancelButton: {
    backgroundColor: '#21262d',
    border: '1px solid #30363d',
    color: '#e6edf3',
  },
  confirmButton: {
    backgroundColor: '#238636',
    border: '1px solid #2ea043',
    color: '#ffffff',
  },
  dangerButton: {
    backgroundColor: '#21262d',
    border: '1px solid rgba(239, 68, 68, 0.55)',
    color: '#ef4444',
  },
};

export const ConfirmProvider: React.FC<ConfirmProviderProps> = ({ children }) => {
  const resolverRef = useRef<((value: boolean) => void) | null>(null);
  const [state, setState] = useState<ConfirmState>({
    isOpen: false,
    title: 'Confirm',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    tone: 'default',
  });

  const close = useCallback((value: boolean): void => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setState((prev: ConfirmState) => ({ ...prev, isOpen: false }));
    if (resolver) resolver(value);
  }, []);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    if (resolverRef.current) {
      const previousResolver = resolverRef.current;
      resolverRef.current = null;
      previousResolver(false);
    }

    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setState({
        isOpen: true,
        title: options.title || 'Confirm',
        message: options.message,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        tone: options.tone || 'default',
      });
    });
  }, []);

  const value = useMemo<ConfirmContextValue>(() => ({ confirm }), [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {state.isOpen && (
        <div
          style={styles.overlay}
          onClick={() => {
            close(false);
          }}
        >
          <div
            style={styles.panel}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
              e.stopPropagation();
            }}
          >
            <div style={styles.header}>{state.title}</div>
            <div style={styles.body}>{state.message}</div>
            <div style={styles.footer}>
              <button
                type="button"
                style={{ ...styles.button, ...styles.cancelButton }}
                onClick={() => {
                  close(false);
                }}
              >
                {state.cancelText}
              </button>
              <button
                type="button"
                style={{
                  ...styles.button,
                  ...(state.tone === 'danger' ? styles.dangerButton : styles.confirmButton),
                }}
                onClick={() => {
                  close(true);
                }}
              >
                {state.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = (): ConfirmContextValue => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within ConfirmProvider');
  }
  return ctx;
};
